import type {
  HarnessChannel,
  HarnessClinic,
  HarnessConversation,
  HarnessConversationSummary,
  HarnessItemResult,
  HarnessTurn,
  StartHarnessRunBody,
} from '@borradh-workspace/api-client/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  ArrowUp,
  Check,
  ChevronsUpDown,
  Instagram,
  MessageCircle,
  MessagesSquare,
  Plus,
  Smartphone,
  Wrench,
} from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { Field, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { Spinner } from '@/components/ui/spinner';
import { Switch } from '@/components/ui/switch';
import { Textarea } from '@/components/ui/textarea';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { queryKeys } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

import {
  useForkHarnessConversation,
  useHarnessClinics,
  useHarnessConversations,
  useHarnessRun,
  useLoadHarnessConversation,
  useStartHarnessRun,
} from '../../api';
import { describeEvent, describeGatedTools } from './harness-result';
import {
  CHANNEL_LABELS,
  InboxBubble,
  InboxChip,
  InboxEvent,
  InboxPane,
  InboxRail,
  InboxRailItem,
  InboxSplit,
  InboxSystem,
  InboxThread,
  Linkify,
  shortTime,
} from './inbox';
import { LeadFormPanel } from './lead-form-panel';

const CHANNELS: {
  value: HarnessChannel;
  label: string;
  icon: typeof MessageCircle;
}[] = [
  { value: 'facebook_messenger', label: 'Messenger', icon: MessageCircle },
  { value: 'whatsapp', label: 'WhatsApp', icon: MessagesSquare },
  { value: 'instagram_dm', label: 'Instagram', icon: Instagram },
  { value: 'sms', label: 'SMS', icon: Smartphone },
];

const NO_AD = '__none__';

const EXAMPLES = [
  'How much is it?',
  'Is the price on the ad still available?',
  'Can I book for Saturday?',
  'Do you do lip filler?',
  'Can I speak to a real person?',
];

type ThreadTurn = {
  /** Saved message id (hosted): any saved message can be continued from. */
  id?: string;
  role: 'user' | 'bot' | 'agent';
  text: string;
  /** This session's run for a Claire reply: what happened around it. */
  item?: HarnessItemResult;
  failed?: boolean;
};

function ClinicPicker({
  clinics,
  value,
  onChange,
}: {
  clinics: HarnessClinic[];
  value: HarnessClinic | null;
  onChange: (clinic: HarnessClinic) => void;
}) {
  const [open, setOpen] = useState(false);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          id="harness-clinic"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
        >
          <span className="truncate">
            {value ? value.name : 'Pick a clinic from your local database'}
          </span>
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-(--radix-popover-trigger-width) p-0">
        <Command>
          <CommandInput placeholder="Search clinics…" />
          <CommandList>
            <CommandEmpty>No clinic matches.</CommandEmpty>
            <CommandGroup>
              {clinics.map((clinic) => (
                <CommandItem
                  key={clinic.id}
                  value={`${clinic.name} ${clinic.id}`}
                  onSelect={() => {
                    onChange(clinic);
                    setOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      'size-4',
                      value?.id === clinic.id ? 'opacity-100' : 'opacity-0'
                    )}
                  />
                  <span className="flex-1 truncate">{clinic.name}</span>
                  <span className="text-muted-foreground text-xs">
                    {clinic.serviceCount} services
                    {clinic.ads.length ? ` · ${clinic.ads.length} ads` : ''}
                  </span>
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function buildMessageRun(input: {
  clinic: HarnessClinic;
  channel: HarnessChannel;
  adMetaId: string | null;
  customerName: string;
  history: HarnessTurn[];
  message: string;
  simulateHandoff: boolean;
  conversationId: string | null;
}): StartHarnessRunBody {
  return {
    kind: 'message',
    organizationId: input.clinic.id,
    channel: input.channel,
    adMetaId: input.adMetaId,
    customerName: input.customerName.trim() || 'Test Customer',
    history: input.history,
    message: input.message.trim(),
    simulateHandoff: input.simulateHandoff,
    conversationId: input.conversationId,
  };
}

const WARN_EVENTS = [
  'grounding_violation',
  'rule_violation',
  'fabrication_detected',
  'suspicious_claim',
];

/** Tools, guards and blocked drafts, as rows under Claire's reply. */
function ReplyDetails({ item }: { item: HarnessItemResult }) {
  const drafts = (item.drafts ?? []).filter((draft) => draft.trim());
  const blocked = drafts.filter((draft) => draft !== item.reply);
  const guards = (item.events ?? [])
    .map((event) => ({ event, text: describeEvent(event) }))
    .filter((entry) => entry.text);
  const gated = describeGatedTools(item.events ?? []);
  const failedChecks = (item.checks ?? []).filter((check) => !check.pass);
  return (
    <>
      {(item.tools ?? []).map((tool, index) => (
        <InboxEvent
          key={`${tool.name}-${index}`}
          tone="tool"
          className={cn(tool.blocked && 'opacity-70')}
        >
          <span className="inline-flex items-center gap-1.5 font-medium">
            <Wrench className="size-3.5" />
            {tool.name}
          </span>
          <span className="opacity-70">
            {tool.blocked
              ? ' · blocked, would act outside the sandbox'
              : tool.simulated
                ? ' · recorded result'
                : ' · ran on the clinic’s data'}
          </span>
        </InboxEvent>
      ))}
      {guards.map(({ event, text }, index) => (
        <InboxEvent
          key={`${event.event}-${index}`}
          tone={WARN_EVENTS.includes(event.event) ? 'warn' : 'plain'}
        >
          {text}
        </InboxEvent>
      ))}
      {gated.map((text) => (
        <InboxEvent key={text} tone="plain">
          {text}
        </InboxEvent>
      ))}
      {blocked.map((draft, index) => (
        <InboxEvent key={`draft-${index}`} tone="warn">
          <span className="font-medium">Draft the checks blocked: </span>
          <span className="line-through opacity-80">{draft}</span>
        </InboxEvent>
      ))}
      {failedChecks.map((check) => (
        <InboxEvent key={check.label} tone="flag">
          {check.label}
        </InboxEvent>
      ))}
      {item.durationMs > 0 ? (
        <span className="self-end text-muted-foreground text-xs tabular-nums">
          {(item.durationMs / 1000).toFixed(1)}s
          {item.modelCalls
            ? ` · ${item.modelCalls} model call${item.modelCalls === 1 ? '' : 's'}`
            : ''}
        </span>
      ) : null}
    </>
  );
}

export function TryMessagePanel({
  disabled,
  liveUnavailableReason,
  hosted = false,
  openConversation = null,
}: {
  disabled: boolean;
  liveUnavailableReason: string | null;
  /** Hosted Harness: conversations are saved and can be continued. */
  hosted?: boolean;
  /** A conversation to open, e.g. one just copied from live traffic. */
  openConversation?: HarnessConversation | null;
}) {
  const queryClient = useQueryClient();
  const { clinics, isLoading } = useHarnessClinics();
  const { conversations, isLoading: loadingSaved } =
    useHarnessConversations(hosted);
  const [clinic, setClinic] = useState<HarnessClinic | null>(null);
  const [channel, setChannel] = useState<HarnessChannel>('facebook_messenger');
  const [adMetaId, setAdMetaId] = useState<string>(NO_AD);
  const [customerName, setCustomerName] = useState('Test Customer');
  const [simulateHandoff, setSimulateHandoff] = useState(false);
  const [draft, setDraft] = useState('');
  const [thread, setThread] = useState<ThreadTurn[]>([]);
  const [pendingRunId, setPendingRunId] = useState<string | null>(null);
  /** Hosted: the saved conversation this thread lives in. */
  const [conversationId, setConversationId] = useState<string | null>(null);
  const { run: pendingRun } = useHarnessRun(pendingRunId);
  /** How the open conversation started, shown at the top of the thread. */
  const [origin, setOrigin] = useState<Pick<
    HarnessConversation,
    'source' | 'leadFormName' | 'leadAnswers'
  > | null>(null);
  const loadConversation = useLoadHarnessConversation();
  const scrollRef = useRef<HTMLDivElement>(null);

  // Default to the clinic with the most setup, so the first run is useful.
  useEffect(() => {
    if (!clinic && clinics.length) {
      const first = clinics[0];
      if (first) {
        setClinic(first);
        setChannel(first.channels[0] ?? 'facebook_messenger');
      }
    }
  }, [clinic, clinics]);

  // Fold the finished reply into the thread.
  // biome-ignore lint/correctness/useExhaustiveDependencies: loadConversation and applySaved are recreated each render; the effect must run once per finished run
  useEffect(() => {
    if (!pendingRun || pendingRun.status === 'running') return;
    const item = pendingRun.items[0];
    setPendingRunId(null);
    // The saved list shows each conversation's latest message.
    void queryClient.invalidateQueries({
      queryKey: queryKeys.adminTerminal.harnessConversations(),
    });
    if (hosted && item?.conversationId && item.status !== 'error') {
      // Reload from the server: every message gets its id (so it can be
      // continued from) and every reply its saved detail.
      void loadConversation(item.conversationId).then(applySaved);
      return;
    }
    if (item?.conversationId) setConversationId(item.conversationId);
    setThread((turns) => [
      ...turns,
      {
        role: 'bot',
        text:
          item?.reply?.trim() ||
          (item?.status === 'error'
            ? (item.error ?? 'The run failed')
            : `(${item?.note ?? 'no reply'})`),
        item,
        failed: item?.status === 'error' || !item?.reply?.trim(),
      },
    ]);
  }, [pendingRun, queryClient, hosted]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll whenever a turn is added or Claire starts thinking
  useEffect(() => {
    const box = scrollRef.current;
    // An empty thread shows the lead form from its top.
    if (box && (thread.length || pendingRunId))
      box.scrollTop = box.scrollHeight;
  }, [thread.length, pendingRunId]);

  const { startRun, isStarting } = useStartHarnessRun({
    onSuccess: (run) => setPendingRunId(run.id),
  });

  const ads = clinic?.ads ?? [];
  const selectedAd = useMemo(
    () => ads.find((ad) => ad.metaAdId === adMetaId) ?? null,
    [ads, adMetaId]
  );
  const { fork, isForking } = useForkHarnessConversation({
    onSuccess: applySaved,
  });

  const busy = isStarting || Boolean(pendingRunId) || isForking;
  const blocked = disabled || Boolean(liveUnavailableReason);

  const send = (text: string) => {
    if (!clinic || !text.trim() || busy || blocked) return;
    const history: HarnessTurn[] = thread
      .filter((turn) => !turn.failed)
      .map((turn) => ({
        role: turn.role === 'user' ? 'user' : 'bot',
        text: turn.text,
      }));
    const body = buildMessageRun({
      clinic,
      channel,
      adMetaId: adMetaId === NO_AD || channel === 'sms' ? null : adMetaId,
      customerName,
      // A saved conversation's history lives on the server; resending it would
      // hit the 30-turn cap on long threads.
      history: conversationId ? [] : history,
      message: text,
      simulateHandoff,
      conversationId,
    });
    setThread((turns) => [...turns, { role: 'user', text: text.trim() }]);
    setDraft('');
    startRun(body);
  };

  const reset = () => {
    setThread([]);
    setPendingRunId(null);
    setConversationId(null);
    setOrigin(null);
  };

  function applySaved(saved: HarnessConversation) {
    const savedClinic = clinics.find((c) => c.id === saved.organizationId);
    if (savedClinic) setClinic(savedClinic);
    setChannel(saved.channel);
    setAdMetaId(
      saved.adMetaId &&
        savedClinic?.ads.some((ad) => ad.metaAdId === saved.adMetaId)
        ? saved.adMetaId
        : NO_AD
    );
    if (saved.customerName) setCustomerName(saved.customerName);
    setThread(
      saved.history.map((turn) => ({
        id: turn.id,
        role: turn.role,
        text: turn.text,
        item: turn.detail ?? undefined,
      }))
    );
    setOrigin({
      source: saved.source,
      leadFormName: saved.leadFormName,
      leadAnswers: saved.leadAnswers,
    });
    setPendingRunId(null);
    setConversationId(saved.id);
  }

  const openSaved = async (summary: HarnessConversationSummary) => {
    if (busy) return;
    applySaved(await loadConversation(summary.id));
  };

  // Opened from elsewhere (live traffic): show it once clinics are loaded.
  // biome-ignore lint/correctness/useExhaustiveDependencies: applySaved reads the latest clinics; open each conversation once
  useEffect(() => {
    if (openConversation && clinics.length) applySaved(openConversation);
  }, [openConversation, clinics.length]);

  const submitLeadForm = (
    body: Parameters<typeof startRun>[0],
    formName: string | null
  ) => {
    if (busy || blocked) return;
    reset();
    if (body.kind === 'lead_form') setChannel(body.channel);
    setOrigin({ source: 'lead_form', leadFormName: formName, leadAnswers: [] });
    startRun(body);
  };

  if (isLoading) {
    return (
      <InboxSplit rail={<Skeleton className="h-96 w-full" />}>
        <Skeleton className="h-96 w-full" />
      </InboxSplit>
    );
  }

  const channelLabel = CHANNEL_LABELS[channel] ?? channel;
  const unsaved = thread.length > 0 && !conversationId;

  const rail = (
    <InboxRail
      label="Saved conversations"
      header={
        <div className="sticky top-0 z-10 border-b bg-card p-2">
          <Button
            variant="outline"
            size="sm"
            className="w-full"
            onClick={reset}
            disabled={busy}
          >
            <Plus />
            New conversation
          </Button>
        </div>
      }
    >
      {unsaved || (thread.length === 0 && !conversationId) ? (
        <InboxRailItem
          title={customerName.trim() || 'Test Customer'}
          subtitle={clinic?.name ?? 'No clinic'}
          time={thread.length ? undefined : 'new'}
          active
          onSelect={() => {}}
          chips={
            <>
              <InboxChip>{channelLabel}</InboxChip>
              <InboxChip tone="agent">
                {thread.length ? 'Not saved yet' : 'Draft'}
              </InboxChip>
            </>
          }
        />
      ) : null}
      {hosted ? (
        loadingSaved ? (
          <div className="space-y-2 p-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        ) : (
          conversations.map((item) => (
            <InboxRailItem
              key={item.id}
              testId="saved-conversation"
              title={item.customerName ?? 'Test Customer'}
              time={shortTime(item.updatedAt)}
              subtitle={item.organizationName}
              active={item.id === conversationId}
              onSelect={() => void openSaved(item)}
              chips={
                <>
                  <InboxChip>
                    {CHANNEL_LABELS[item.channel] ?? item.channel}
                  </InboxChip>
                  <InboxChip tone="tool">
                    {item.turns} {item.turns === 1 ? 'message' : 'messages'}
                  </InboxChip>
                  {item.lastMessage ? (
                    <span className="block w-full truncate text-muted-foreground text-xs">
                      {item.lastMessage}
                    </span>
                  ) : null}
                </>
              }
            />
          ))
        )
      ) : (
        <p className="p-3 text-muted-foreground text-xs">
          Conversations are saved on the hosted Harness (app.borrah.com). Here
          they last until you start a new one.
        </p>
      )}
    </InboxRail>
  );

  return (
    <InboxSplit rail={rail}>
      <InboxPane
        title={customerName.trim() || 'Test Customer'}
        meta={[
          clinic?.name ?? 'No clinic',
          channelLabel,
          conversationId
            ? 'Saved'
            : hosted
              ? 'Saved after the first reply'
              : 'Not saved',
        ].join(' · ')}
        footer={
          <form
            className="flex items-end gap-2 border-t p-3"
            onSubmit={(event) => {
              event.preventDefault();
              send(draft);
            }}
          >
            <Textarea
              aria-label="Customer message"
              placeholder={
                liveUnavailableReason ??
                (clinic
                  ? 'Type what the customer says…'
                  : 'Pick a clinic first')
              }
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter' && !event.shiftKey) {
                  event.preventDefault();
                  send(draft);
                }
              }}
              rows={1}
              className="max-h-32 min-h-10 resize-none"
              disabled={!clinic || blocked}
            />
            <Button
              type="submit"
              size="icon"
              className="shrink-0 rounded-full"
              disabled={!clinic || !draft.trim() || busy || blocked}
              aria-label="Send"
            >
              <ArrowUp className="size-4" />
            </Button>
          </form>
        }
      >
        <div className="grid gap-3 border-b bg-muted/30 px-4 py-3 sm:grid-cols-2 sm:px-5 xl:grid-cols-[minmax(0,1.4fr)_auto_minmax(0,1fr)_minmax(0,1fr)]">
          <Field>
            <FieldLabel htmlFor="harness-clinic">Clinic</FieldLabel>
            <ClinicPicker
              clinics={clinics}
              value={clinic}
              onChange={(next) => {
                setClinic(next);
                setAdMetaId(NO_AD);
                setChannel(next.channels[0] ?? 'facebook_messenger');
                reset();
              }}
            />
          </Field>
          <Field>
            <FieldLabel>Channel</FieldLabel>
            <ToggleGroup
              type="single"
              variant="outline"
              value={channel}
              onValueChange={(value) => {
                if (value) {
                  setChannel(value as HarnessChannel);
                  // Ads open Messenger, Instagram and WhatsApp chats, never SMS.
                  if (value === 'sms') setAdMetaId(NO_AD);
                  reset();
                }
              }}
            >
              {CHANNELS.map(({ value, label, icon: Icon }) => (
                <ToggleGroupItem
                  key={value}
                  value={value}
                  aria-label={label}
                  title={label}
                >
                  <Icon className="size-4" />
                </ToggleGroupItem>
              ))}
            </ToggleGroup>
          </Field>
          <Field>
            <FieldLabel htmlFor="harness-ad">They clicked an ad</FieldLabel>
            <Select
              value={adMetaId}
              onValueChange={(value) => {
                setAdMetaId(value);
                reset();
              }}
              disabled={channel === 'sms' || ads.length === 0}
            >
              <SelectTrigger id="harness-ad" className="w-full">
                <SelectValue placeholder="No ad" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NO_AD}>
                  {ads.length ? 'No ad' : 'This clinic has no ads'}
                </SelectItem>
                {ads.map((ad) => (
                  <SelectItem key={ad.metaAdId} value={ad.metaAdId}>
                    {ad.headline || ad.name || ad.metaAdId}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field>
            <FieldLabel htmlFor="harness-customer">Customer name</FieldLabel>
            <Input
              id="harness-customer"
              value={customerName}
              onChange={(event) => setCustomerName(event.target.value)}
              onBlur={() => {
                if (!conversationId) reset();
              }}
            />
          </Field>
          <div className="flex items-center gap-2 text-muted-foreground text-xs sm:col-span-2 xl:col-span-4">
            <Switch
              id="harness-handoff"
              checked={simulateHandoff}
              onCheckedChange={setSimulateHandoff}
              aria-label="Let handoffs happen"
            />
            <label htmlFor="harness-handoff">
              Let handoffs happen: when Claire hands over to a human, mark it in
              the copy instead of blocking it. No one is notified either way.
            </label>
          </div>
        </div>

        <div ref={scrollRef} className="h-[540px] overflow-y-auto">
          <InboxThread className="min-h-full">
            {origin?.source === 'lead_form' ? (
              <InboxSystem>
                Submitted the lead form
                {origin.leadFormName ? ` “${origin.leadFormName}”` : ''}
                {origin.leadAnswers.length ? (
                  <span className="mt-1 block text-left">
                    {origin.leadAnswers.map((answer) => (
                      <span key={answer.question} className="block">
                        <span className="capitalize">{answer.question}</span>:{' '}
                        <span className="text-foreground">{answer.answer}</span>
                      </span>
                    ))}
                  </span>
                ) : null}
              </InboxSystem>
            ) : origin?.source === 'live' ? (
              <InboxSystem>
                Copied from a real conversation. From here on it’s a test:
                nothing reaches the customer.
              </InboxSystem>
            ) : null}
            {selectedAd?.primaryText ? (
              <InboxSystem>
                Came from the ad: “{selectedAd.primaryText.slice(0, 160)}
                {selectedAd.primaryText.length > 160 ? '…' : ''}”
              </InboxSystem>
            ) : null}
            {thread.length === 0 && !busy ? (
              <div className="flex flex-1 flex-col items-center justify-center gap-5 py-10 text-center">
                <div className="space-y-3">
                  <p className="max-w-sm text-muted-foreground text-sm">
                    Write what the customer says. Claire answers with today’s
                    code and the real AI, against a copy of this clinic. Nothing
                    is sent to anyone.
                  </p>
                  <div className="flex flex-wrap justify-center gap-1.5">
                    {EXAMPLES.map((example) => (
                      <button
                        key={example}
                        type="button"
                        className="rounded-full border bg-background px-3 py-1 text-xs transition-colors hover:bg-muted disabled:opacity-50"
                        onClick={() => send(example)}
                        disabled={!clinic || busy || blocked}
                      >
                        {example}
                      </button>
                    ))}
                  </div>
                </div>
                {clinic ? (
                  hosted ? (
                    <>
                      <p className="text-muted-foreground text-xs uppercase tracking-wide">
                        or
                      </p>
                      <LeadFormPanel
                        key={clinic.id}
                        clinic={clinic}
                        customerName={customerName}
                        disabled={blocked}
                        busy={busy}
                        onSubmit={submitLeadForm}
                      />
                    </>
                  ) : (
                    <p className="max-w-sm text-muted-foreground text-xs">
                      Lead form submissions run on the hosted Harness
                      (app.borrah.com), against its own database.
                    </p>
                  )
                ) : null}
              </div>
            ) : (
              thread.map((turn, index) => {
                const onContinue =
                  hosted && turn.id && conversationId && !busy
                    ? () => {
                        if (turn.id && conversationId) {
                          fork({ id: conversationId, messageId: turn.id });
                        }
                      }
                    : undefined;
                if (turn.role === 'user') {
                  return (
                    <InboxBubble
                      key={turn.id ?? index}
                      from="user"
                      onContinue={onContinue}
                    >
                      <Linkify text={turn.text} />
                    </InboxBubble>
                  );
                }
                if (turn.failed && turn.item?.status === 'error') {
                  return (
                    <InboxEvent key={turn.id ?? index} tone="flag">
                      {turn.text}
                    </InboxEvent>
                  );
                }
                return (
                  <div
                    key={turn.id ?? index}
                    className="flex flex-col items-end gap-1.5"
                  >
                    <InboxBubble
                      from={turn.role === 'agent' ? 'agent' : 'bot'}
                      onContinue={onContinue}
                      className={cn(
                        turn.failed && 'italic text-muted-foreground'
                      )}
                    >
                      <Linkify text={turn.text} />
                    </InboxBubble>
                    {turn.item ? <ReplyDetails item={turn.item} /> : null}
                  </div>
                );
              })
            )}
            {busy ? (
              <InboxSystem>
                <span className="inline-flex items-center gap-2">
                  <Spinner className="size-3.5" />
                  {isForking
                    ? 'Copying the conversation…'
                    : 'Claire is thinking…'}
                </span>
              </InboxSystem>
            ) : null}
          </InboxThread>
        </div>
      </InboxPane>
    </InboxSplit>
  );
}
